import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  Link,
  Button,
  Flex,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import Logo from '../components/Logo';

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  useEffect(() => {
    // Check for query parameters - URLs with data parameters should not be indexed
    const searchParams = new URLSearchParams(window.location.search);
    const hasDataParam = searchParams.has('data') || searchParams.has('encrypted');
    
    // Set canonical tag for SEO (always use clean URL without query params)
    let canonicalLink = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute('href', 'https://abmymix.com/privacy');
    
    // Set robots meta tag
    let robotsTag = document.querySelector('meta[name="robots"]') as HTMLMetaElement;
    if (!robotsTag) {
      robotsTag = document.createElement('meta');
      robotsTag.setAttribute('name', 'robots');
      document.head.appendChild(robotsTag);
    }
    
    // If URL has data parameter, set noindex; otherwise allow indexing
    if (hasDataParam) {
      robotsTag.setAttribute('content', 'noindex, nofollow');
    } else {
      robotsTag.setAttribute('content', 'index, follow');
    }
  }, []);

  return (
    <Box minH="100vh" bg="gray.900" color="gray.100">
      <Container maxW="container.lg" py={8}>
        <Flex justify="space-between" align="center" mb={8}>
          <Button
            onClick={() => navigate('/')}
            variant="ghost"
            colorScheme="gray"
            size="sm"
          >
            ← Back to ABmyMix
          </Button>
          <Box
            as="button"
            onClick={() => navigate('/')}
            bg="transparent"
            border="none"
            cursor="pointer"
            _hover={{ opacity: 0.9 }}
          >
            <Box
              w="120px"
              h="40px"
            >
              <Logo />
            </Box>
          </Box>
        </Flex>

        <VStack spacing={6} align="stretch">
          <Heading as="h1" size="xl" textAlign="center" color="gray.100">
            Privacy Policy
          </Heading>
          
          <Text fontSize="sm" color="gray.400" textAlign="center">
            Last updated: January 2025
          </Text>

          <VStack spacing={6} align="stretch" mt={8}>
            <Box>
              <Heading as="h2" size="lg" mb={4} color="gray.200">
                Overview
              </Heading>
              <Text>
                ABmyMix is an audio comparison tool designed with privacy in mind. This Privacy Policy explains how we handle your data when you use our service.
              </Text>
            </Box>

            <Box>
              <Heading as="h2" size="lg" mb={4} color="gray.200">
                Local Audio Processing
              </Heading>
              <Text mb={3}>
                <strong>Your audio files are processed entirely in your browser.</strong> When you upload audio files to ABmyMix:
              </Text>
              <VStack align="stretch" spacing={2} pl={4}>
                <Text>• Files are processed locally using your device&apos;s computing power</Text>
                <Text>• Audio data never leaves your device unless you explicitly choose to share via Dropbox</Text>
                <Text>• No audio content is transmitted to our servers</Text>
                <Text>• Files are temporarily stored in your browser&apos;s memory during use</Text>
              </VStack>
            </Box>

            <Box>
              <Heading as="h2" size="lg" mb={4} color="gray.200">
                Dropbox Integration
              </Heading>
              <Text mb={3}>
                When you choose to use Dropbox integration features:
              </Text>
              <VStack align="stretch" spacing={2} pl={4}>
                <Text>• We use Dropbox&apos;s official API to access files you explicitly select</Text>
                <Text>• We only request access to files you choose to share</Text>
                <Text>• Dropbox handles all file storage and access according to their privacy policy</Text>
                <Text>• Share links contain Dropbox file URLs that allow access to your shared files</Text>
                <Text>• You control who has access to shared setups by controlling the share link</Text>
              </VStack>
              <Text mt={3}>
                Please review <Link href="https://www.dropbox.com/privacy" color="blue.400" isExternal>Dropbox&apos;s Privacy Policy</Link> for details on how they handle your data.
              </Text>
            </Box>

            <Box>
              <Heading as="h2" size="lg" mb={4} color="gray.200">
                Data Storage and Sharing
              </Heading>
              <Text mb={3}>
                <strong>Local Storage:</strong>
              </Text>
              <VStack align="stretch" spacing={2} pl={4} mb={4}>
                <Text>• User preferences and settings are stored locally in your browser</Text>
                <Text>• No personal information is stored on our servers</Text>
                <Text>• You can clear this data by clearing your browser&apos;s local storage</Text>
              </VStack>
              
              <Text mb={3}>
                <strong>Shared Setups:</strong>
              </Text>
              <VStack align="stretch" spacing={2} pl={4}>
                <Text>• When you create a shared setup, file URLs become part of the share link</Text>
                <Text>• Anyone with the share link can access the referenced files</Text>
                <Text>• Only share setups with people you trust with direct file access</Text>
                <Text>• We do not store or have access to the content of shared files</Text>
              </VStack>
            </Box>

            <Box>
              <Heading as="h2" size="lg" mb={4} color="gray.200">
                Analytics and Tracking
              </Heading>
              <Text mb={3}>
                We use Google Analytics to understand how our service is used:
              </Text>
              <VStack align="stretch" spacing={2} pl={4}>
                <Text>• We collect anonymous usage statistics</Text>
                <Text>• No personal information or audio content is tracked</Text>
                <Text>• You can opt out using browser settings or ad blockers</Text>
              </VStack>
            </Box>

            <Box>
              <Heading as="h2" size="lg" mb={4} color="gray.200">
                Your Rights
              </Heading>
              <VStack align="stretch" spacing={2} pl={4}>
                <Text>• You control all audio files and data processed by ABmyMix</Text>
                <Text>• You can delete local data by clearing your browser storage</Text>
                <Text>• You can revoke Dropbox access through your Dropbox account settings</Text>
                <Text>• You control who has access to your shared setups</Text>
              </VStack>
            </Box>

            <Box>
              <Heading as="h2" size="lg" mb={4} color="gray.200">
                Security
              </Heading>
              <Text>
                We implement security measures to protect your privacy, including HTTPS encryption for all communications and secure integration with Dropbox&apos;s API. However, the security of shared setups depends on how you manage and distribute share links.
              </Text>
            </Box>

            <Box>
              <Heading as="h2" size="lg" mb={4} color="gray.200">
                Changes to This Policy
              </Heading>
              <Text>
                We may update this Privacy Policy from time to time. We will notify users of any material changes by updating the &quot;Last updated&quot; date at the top of this policy.
              </Text>
            </Box>

            <Box>
              <Heading as="h2" size="lg" mb={4} color="gray.200">
                Contact
              </Heading>
              <Text>
                If you have questions about this Privacy Policy, please contact us through the feedback feature in the application.
              </Text>
            </Box>
          </VStack>
        </VStack>
      </Container>
    </Box>
  );
};

export default PrivacyPolicy; 